import React, {
  useState,
  useLayoutEffect,
  useEffect,
  useCallback,
  useMemo,
} from 'react'
import {
  Container,
  Row,
  Col,
  ButtonToolbar,
  Button as BButton,
} from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useParams, useHistory } from 'react-router'
import { useSelector, useDispatch } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faSave,
  faPlusCircle,
  faPollH,
  faArrowLeft,
  faSpinner,
  faPaperclip,
  faPhotoVideo,
  faFileAlt,
  faSortAlt,
  faUndo,
  faBallotCheck,
  faCogs,
} from '@fortawesome/pro-solid-svg-icons'
import * as fromBlockActions from 'store/actions/blocks.action'
import * as fromLessonActions from 'store/actions/lessons-admin.action'
import { IMainState } from 'store/states'
import { IBlockInstanceModel } from 'models/block.model'
import { BlockActionEnum } from 'enum/block-action.enum'
import { BlockTypes } from 'enum'
import Loader from 'components/ui/loader/Loader'
import Breadcrumb, { BreadcrumbItem } from 'design-system/breadcrumb'
import Button from 'design-system/button'
import { ButtonSizes, ButtonVariant } from 'design-system/button/src/Button'
import CommonToolTip from 'components/ui/tooltip/commonToolTip'
import VideoConference from 'assets/icons/VideoConference'
import * as fromCourseActions from 'store/actions/courses.action'
import styles from './Index.module.scss'
import BlockType from './components/block-type/BlockType'
import BlockTypeButton from './components/block-type-button/BlockTypeButton'
import BlockList from './components/blocks-list/BlockList'
import useActions from './useActions'
import ConfirmationDelete from './components/ConfirmationDelete'
import useIntegrations from 'hooks/useIntegrations'
import Wrapper from 'components/reusables/wrapper'

type Params = Record<'courseId' | 'lessonId', string>

const Index: React.FC = () => {
  const { t } = useTranslation()

  const { courseId, lessonId } = useParams<Params>()
  const dispatch = useDispatch()
  const history = useHistory()

  useEffect(() => {
    dispatch({ type: fromCourseActions.GET_ADMIN_COURSE, payload: courseId })
  }, [courseId, dispatch])

  const { integrationZoom } = useIntegrations()

  const { list, loading } = useSelector(({ blocks }: IMainState) => blocks)
  const editBlock = useSelector(({ blocks }: IMainState) => blocks).edit
  const [showOptions, setShowOptions] = useState(false)
  const [createOrEdit, setCreateOrEdit] = useState(BlockActionEnum.Create)
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] =
    useState(false)
  const [listAux, setListAux] = useState<IBlockInstanceModel[]>([])

  const {
    handleDelete,
    handleEdit,
    handleSelectedOptionChange,
    handleSubmit,
    handleSaveOrder,
    setOrder,
    setDraggable,
    setSelectedOption,
    setSelected,
    setLessonData,
    states: {
      loading: buttonLoading,
      draggable,
      selectedOption,
      selected,
      lessonData,
    },
  } = useActions({
    lessonId: +lessonId,
    onFinish: () => {
      setShowOptions(false)
    },
    onEdit: () => {
      setCreateOrEdit(BlockActionEnum.Edit)
    },
  })

  const hasEvaluation = useMemo(
    () => list.some((block) => block.blockType === BlockTypes.BlockEvaluation),
    [list]
  )

  const hasScorm = useMemo(
    () => list.some((block) => block.blockType === BlockTypes.BlockScorm),
    [list]
  )

  useEffect(() => {
    setListAux(list.slice())
  }, [list])

  useLayoutEffect(() => {
    if (lessonId) {
      dispatch({ type: fromLessonActions.GET_LESSON, lessonId })
      dispatch({ type: fromBlockActions.GET_LESSON_BLOCKS_LIST, lessonId })
    }
    return () => {
      dispatch({ type: fromBlockActions.CLEAR_EDIT })
    }
  }, [dispatch, lessonId])

  const blockInstance = useMemo(
    () => list.find((block) => block.id === selected),
    [list, selected]
  )

  const onDeleteClick = (id: number) => {
    setShowDeleteConfirmationModal(true)
    setSelected(id)
  }

  const handleCancel = useCallback(() => {
    setSelectedOption(null)
    dispatch({ type: fromBlockActions.CLEAR_EDIT })
    setCreateOrEdit(BlockActionEnum.Create)
  }, [dispatch, setSelectedOption])

  const handleCancelDraggable = () => {
    setListAux(list.slice())
    setDraggable(false)
  }

  const handleSetDraggable = () => {
    setDraggable(true)
    setSelectedOption(null)
    setSelected(0)
    setCreateOrEdit(BlockActionEnum.Create)
    setShowOptions(false)
    dispatch({ type: fromBlockActions.CLEAR_EDIT })
  }

  const videoConferenceEnable = !!integrationZoom?.active

  return loading ? (
    <Loader overlay color />
  ) : (
    <Wrapper shadow={true}>
      <Row className="pb-4" style={{ margin: '-11px 0 0 0' }}>
        <Breadcrumb separator=">">
          <BreadcrumbItem
            href="/administration/courses"
            text={t('administration.blocks.breadcrum.init')}
          />
          <BreadcrumbItem
            href={`/administration/courses/${courseId}/edit`}
            text={t('administration.blocks.breadcrum.courseData')}
          />
          <BreadcrumbItem
            href={`/administration/courses/${courseId}/lessons`}
            text={t('administration.blocks.breadcrum.lessons')}
          />
          <BreadcrumbItem text={t('administration.blocks.breadcrum.blocks')} />
        </Breadcrumb>
      </Row>
      <Row className={'justify-content-center mb-4'}>
        <Col sm={12} md={11} lg={9}>
          <input
            className={styles.lessonName}
            placeholder={t('administration.lessons.lessonName')}
            value={lessonData['name']}
            onChange={(e: any) =>
              setLessonData({ ...lessonData, ['name']: e.target.value })
            }
          />
        </Col>
      </Row>
      <Row className={'justify-content-center pt-5'}>
        <Col sm={12} md={11} lg={9}>
          <div className="d-flex justify-content-between align-items-end">
            <div>
              <h1 className={`${styles.title} mb-0`}>
                {t('administration.blocks.contentBlock')}
              </h1>
            </div>
            <div>
              {!draggable ? (
                <Button
                  className={`m-0 ${styles.orderButton} ${styles.borderFix}`}
                  variant={ButtonVariant.Secondary}
                  size={ButtonSizes.Small}
                  onClick={handleSetDraggable}
                >
                  <FontAwesomeIcon className="mr-2" icon={faSortAlt} />
                  {t('administration.blocks.buttons.moveBlocks')}
                </Button>
              ) : (
                <>
                  <Button
                    className={`m-0 mr-1 ${styles.orderButton}`}
                    outline={true}
                    variant={ButtonVariant.Secondary}
                    size={ButtonSizes.Small}
                    onClick={handleCancelDraggable}
                  >
                    <FontAwesomeIcon className="mr-2" icon={faUndo} />
                    {t('administration.blocks.buttons.cancel')}
                  </Button>
                  <Button
                    className={`m-0 ml-1 ${styles.buttonSave} ${styles.orderButton}`}
                    outline={true}
                    variant={ButtonVariant.Success}
                    size={ButtonSizes.Small}
                    onClick={handleSaveOrder}
                  >
                    <FontAwesomeIcon className="mr-2" icon={faSave} />
                    {t('administration.blocks.buttons.saveOrder')}
                  </Button>
                </>
              )}
            </div>
          </div>
          <BlockList
            active={editBlock.instanceId || undefined}
            draggable={draggable}
            blocks={listAux}
            onDelete={onDeleteClick}
            onEdit={(block: IBlockInstanceModel) => handleEdit(block)}
            onOrderChange={(blocks: IBlockInstanceModel[]) => setOrder(blocks)}
          />
        </Col>
      </Row>
      <Row className={`justify-content-center ${styles.lastItem}`}>
        <Col sm={12} md={11} lg={9} className="pb-3">
          <BButton
            className={styles.addBlockButton}
            disabled={
              selectedOption !== null ||
              createOrEdit === BlockActionEnum.Edit ||
              draggable
            }
            onClick={() => setShowOptions(!showOptions)}
          >
            {t(`administration.blocks.${createOrEdit}`)}
            <FontAwesomeIcon icon={faPlusCircle} />
          </BButton>
          {showOptions && (
            <Container
              fluid
              className={`${styles.blockOptionsContainer} mt-2 `}
            >
              <div className={'d-flex flex-column align-items-center'}>
                <CommonToolTip
                  text={t('administration.blocks.tooltips.richText')}
                  position="top"
                >
                  <div>
                    <BlockTypeButton
                      text={t('administration.blocks.type.richText.name')}
                      icon={<FontAwesomeIcon icon={faFileAlt} />}
                      onClick={() =>
                        handleSelectedOptionChange(BlockTypes.BlockRichText)
                      }
                    />
                  </div>
                </CommonToolTip>
              </div>
              <div className={'d-flex flex-column align-items-center'}>
                <CommonToolTip
                  text={t('administration.blocks.tooltips.multimedia')}
                  position="top"
                >
                  <div>
                    <BlockTypeButton
                      text={t('administration.blocks.type.multimedia.name')}
                      icon={<FontAwesomeIcon icon={faPhotoVideo} />}
                      onClick={() =>
                        handleSelectedOptionChange(BlockTypes.BlockMultimedia)
                      }
                    />
                  </div>
                </CommonToolTip>
              </div>
              <div>
                <CommonToolTip
                  text={t('administration.blocks.tooltips.survey')}
                  position="top"
                >
                  <div>
                    <BlockTypeButton
                      text={t('administration.blocks.type.survey.name')}
                      icon={<FontAwesomeIcon icon={faPollH} />}
                      onClick={() =>
                        handleSelectedOptionChange(BlockTypes.BlockSurvey)
                      }
                    />
                  </div>
                </CommonToolTip>
              </div>
              <div>
                <CommonToolTip
                  text={t('administration.blocks.tooltips.task')}
                  position="top"
                >
                  <div>
                    <BlockTypeButton
                      text={t('administration.blocks.type.task.name')}
                      icon={<FontAwesomeIcon icon={faPaperclip} />}
                      onClick={() =>
                        handleSelectedOptionChange(BlockTypes.BlockTask)
                      }
                    />
                  </div>
                </CommonToolTip>
              </div>
              <div>
                <CommonToolTip
                  text={t(
                    `administration.blocks.tooltips.${
                      hasEvaluation ? 'evaluationDisabled' : 'evaluation'
                    }`
                  )}
                  position="top"
                >
                  <div>
                    <BlockTypeButton
                      text={t('administration.blocks.type.evaluation.name')}
                      icon={<FontAwesomeIcon icon={faBallotCheck} />}
                      className={`${styles.evaluationBlock} ${
                        hasEvaluation ? styles.disabled : ''
                      } `}
                      disabled={hasEvaluation}
                      onClick={() =>
                        handleSelectedOptionChange(BlockTypes.BlockEvaluation)
                      }
                    />
                  </div>
                </CommonToolTip>
              </div>
              <div className={'d-flex flex-column align-items-center'}>
                <CommonToolTip
                  text={t(
                    `administration.blocks.tooltips.${
                      hasScorm ? 'scormDisabled' : 'scorm'
                    }`
                  )}
                  position="top"
                >
                  <div>
                    <BlockTypeButton
                      disabled={hasScorm}
                      className={`${hasScorm ? styles.disabled : ''} `}
                      text={t('administration.blocks.type.scorm.name')}
                      icon={<FontAwesomeIcon icon={faCogs} />}
                      onClick={() =>
                        handleSelectedOptionChange(BlockTypes.BlockScorm)
                      }
                    />
                  </div>
                </CommonToolTip>
              </div>
              {videoConferenceEnable && (
                <div>
                  <CommonToolTip
                    text={t(`administration.blocks.tooltips.videoConference`)}
                    position="top"
                  >
                    <div>
                      <BlockTypeButton
                        text={t(
                          'administration.blocks.type.videoConference.name'
                        )}
                        icon={<VideoConference />}
                        className={`${styles.videoConferenceBlock}`}
                        onClick={() =>
                          handleSelectedOptionChange(BlockTypes.BlockMeeting)
                        }
                      />
                    </div>
                  </CommonToolTip>
                </div>
              )}
            </Container>
          )}
          {selectedOption && (
            <BlockType
              option={selectedOption}
              onCancel={handleCancel}
              editing={createOrEdit === BlockActionEnum.Edit}
            />
          )}
        </Col>
      </Row>
      <Row className={styles.actionButtons}>
        <Col>
          <ButtonToolbar className="float-left ">
            <Button
              size={ButtonSizes.Small}
              variant={ButtonVariant.Secondary}
              disabled={draggable}
              onClick={() =>
                !buttonLoading &&
                !draggable &&
                history.push(`/administration/courses/${courseId}/lessons`)
              }
            >
              <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
              {t('administration.lessons.buttons.back')}
            </Button>
          </ButtonToolbar>
          <ButtonToolbar className="float-right">
            {!!lessonData['name'] && (
              <Button
                disabled={buttonLoading || draggable}
                variant={ButtonVariant.Success}
                size={ButtonSizes.Small}
                onClick={() => !buttonLoading && !draggable && handleSubmit()}
              >
                {buttonLoading ? (
                  <FontAwesomeIcon icon={faSpinner} spin className="mr-1" />
                ) : (
                  <FontAwesomeIcon icon={faSave} className="mr-2" />
                )}
                {t('administration.blocks.buttons.save')}
              </Button>
            )}
            {!lessonData['name'] && (
              <CommonToolTip
                text={t('administration.blocks.buttons.saveTooltip')}
                position={'top'}
              >
                <Button
                  disabled={
                    buttonLoading || draggable || !lessonData['name'].length
                  }
                  variant={ButtonVariant.Success}
                  size={ButtonSizes.Small}
                  onClick={() => !buttonLoading && !draggable && handleSubmit()}
                >
                  {buttonLoading ? (
                    <FontAwesomeIcon icon={faSpinner} spin className="mr-1" />
                  ) : (
                    <FontAwesomeIcon icon={faSave} className="mr-2" />
                  )}
                  {t('administration.blocks.buttons.save')}
                </Button>
              </CommonToolTip>
            )}
          </ButtonToolbar>
        </Col>
      </Row>
      <ConfirmationDelete
        selected={blockInstance}
        show={showDeleteConfirmationModal}
        onAccept={() => {
          setShowDeleteConfirmationModal(false)
          handleDelete()
        }}
        onClose={() => setShowDeleteConfirmationModal(false)}
      />
    </Wrapper>
  )
}

export default Index
